import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFirstRecordAt } from "../hooks/useFirstRecordAt";
import { useResizableHeight } from "../hooks/useResizableHeight";
import { LiveDataRow } from "../models/LiveDataRow";
import { buildChecklistReport } from "../utilities/checklistExport";
import { buildChecklistGroups, ChecklistGroup } from "../utilities/checklistGroups";
import { toChecklistPosition } from "../utilities/checklistPosition";
import { ChecklistLine } from "./ChecklistLine";
import { ChecklistTellTales } from "./ChecklistTellTales";
import { StaleAfterHoursSetting } from "./StaleAfterHoursSetting";
import { TellTaleSizeSetting } from "./TellTaleSizeSetting";
import { VehicleIdentification } from "./VehicleIdentification";
import { ExcelExportButton } from "@/components/export/ExcelExportButton";
import { OdometerDecimalsButton } from "@/components/settings/OdometerDecimalsButton";
import { useDecodeVins } from "@/hooks/devices/useDecodeVins";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useOdometerDecimals } from "@/hooks/userState/useOdometerDecimals";
import { useShowTellTales } from "@/hooks/userState/useShowTellTales";
import { useStaleAfterHours } from "@/hooks/userState/useStaleAfterHours";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { DeviceActivity, deviceActivity } from "@/utilities/deviceActivity";

// Loaded on first opening, so the checklist does not pull Leaflet into every page that shows it.
// Not through mapLazy: that takes the import promise itself, which starts loading at once.
const LiveLocationMap = lazy(() =>
  import("./LiveLocationMap").then(module => ({ default: module.LiveLocationMap })),
);

/** How often the live position is re-read while the map is open, if the page asks for no faster. */
const MAP_REFETCH_MS = 30_000;
const MAP_HEIGHT_PX = 320;
const MAP_MIN_HEIGHT_PX = 200;

interface Props {
  device?: DeviceWithVehicleInfo | null;
  /** Readings from the live window. */
  rows: LiveDataRow[];
  /** Readings from the last 24 hours, used where the live window has nothing. */
  fallbackRows?: LiveDataRow[];
  /** The last reading of the odometers however old, for when even the 24 hours have none. */
  lastKnownRows?: LiveDataRow[];
  /** The last odometers are still being searched for; their lines would read "not found". */
  lastKnownLoading?: boolean;
  /** Geotab's GPS-kept odometer at the end of the period. */
  gpsOdometerRows?: LiveDataRow[];
  /** Start of the period; values from before it turn red past the user's limit. Now where left out. */
  periodStart?: Date;
  fallbackLoading?: boolean;
  /** Diagnostic names are still being resolved, so a name-matched line may be a false miss. */
  diagnosticsLoading?: boolean;
  /** The 24 hour history failed to load; the live readings are still shown. */
  fallbackError?: boolean;
  isError?: boolean;
  /**
   * How often the vehicle's status (driving/stopped, GPS fix) is re-read, in milliseconds; 0 reads
   * it once per selected vehicle. The page's own refresh, so the status does not lag the readings.
   */
  statusRefreshMs?: number;
}

/** A stable empty default, so leaving the history out does not rebuild the groups every render. */
const NO_ROWS: LiveDataRow[] = [];

/**
 * The checklist an installer works through before signing a vehicle off.
 *
 * Every line says whether the value was reported and what it was. A value that only turned up in
 * the last 24 hours is marked as such rather than shown as a plain tick: a vehicle parked
 * overnight reports nothing live, and "reported yesterday" is a different answer from
 * "reporting now" to someone verifying an installation.
 */
export function LiveDataChecklist({
  device,
  rows,
  fallbackRows = NO_ROWS,
  lastKnownRows = NO_ROWS,
  lastKnownLoading = false,
  gpsOdometerRows = NO_ROWS,
  periodStart,
  fallbackLoading = false,
  diagnosticsLoading = false,
  fallbackError = false,
  isError = false,
  statusRefreshMs = 0,
}: Props) {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const { decimals: odometerDecimals } = useOdometerDecimals();
  const { hours: staleAfterHours } = useStaleAfterHours();
  const { enabled: showTellTales, setEnabled: setShowTellTales } = useShowTellTales();

  const { heightPx, resizing, targetRef, handleProps } = useResizableHeight(isExpanded);

  // What the VIN says about the vehicle, from Geotab's own read-out. One VIN, and the answer is
  // cached per VIN, so selecting a vehicle twice asks once.
  const vins = useMemo(
    () => (device?.vehicleIdentificationNumber ? [device.vehicleIdentificationNumber] : []),
    [device?.vehicleIdentificationNumber],
  );
  const { data: vinDetails } = useDecodeVins(vins);
  const vinDetail = vinDetails?.[0];

  const [showMap, setShowMap] = useState(false);
  const deviceIds = useMemo(() => (device?.id ? [device.id] : []), [device?.id]);
  // Polled at the page's pace, and at least every thirty seconds while someone watches the map.
  // Never while folded away: the fleet check mounts one checklist per expanded row, and each poll
  // spends the shared Geotab call budget.
  const refreshMs = !isExpanded
    ? 0
    : showMap
      ? Math.min(statusRefreshMs || MAP_REFETCH_MS, MAP_REFETCH_MS)
      : statusRefreshMs;
  const {
    data: statusInfos,
    isLoading: positionLoading,
    isFetching: statusFetching,
    isError: positionError,
    dataUpdatedAt: statusUpdatedAt,
    refetch: refetchStatus,
  } = useStatusInfosByIds(deviceIds, [], refreshMs);
  const statusInfo = device?.id ? statusInfos?.[device.id] : undefined;
  const position = useMemo(() => toChecklistPosition(statusInfo), [statusInfo]);
  const groupIds = useMemo(
    () => (device?.groups ?? []).flatMap(group => (group.id ? [group.id] : [])),
    [device?.groups],
  );
  const { data: firstRecordAt, isLoading: firstRecordLoading } = useFirstRecordAt(device?.id);

  // Whether the vehicle is moving changes by the second, so an answer cached from an earlier look
  // at this vehicle does not count: on selection the status is asked for again, and shown as
  // unknown until that answer is in.
  const [selectedAt, setSelectedAt] = useState<{ deviceId: string; at: number } | null>(null);
  useEffect(() => {
    if (!device?.id || selectedAt?.deviceId === device.id) {
      return;
    }
    setSelectedAt({ deviceId: device.id, at: Date.now() });
    // A vehicle seen for the first time is being fetched already.
    if (!statusFetching) {
      void refetchStatus();
    }
  }, [device?.id, selectedAt, statusFetching, refetchStatus]);
  const activity: DeviceActivity =
    selectedAt?.deviceId === device?.id && statusUpdatedAt >= (selectedAt?.at ?? Infinity)
      ? deviceActivity(statusInfo, positionError)
      : positionError
        ? "error"
        : "unknown";

  const isPending =
    fallbackLoading ||
    diagnosticsLoading ||
    positionLoading ||
    lastKnownLoading ||
    firstRecordLoading;
  // A sheet written now would list every check as not found, and it is filed as the record.
  const exportBlocked = isError || fallbackError || positionError || isPending;

  const effectiveFallbackRows = fallbackLoading ? NO_ROWS : fallbackRows;

  const groups = useMemo(
    () =>
      buildChecklistGroups({
        device,
        rows,
        fallbackRows: effectiveFallbackRows,
        position,
        lastKnownRows,
        gpsOdometerRows,
        // The live page has no period start; its live window ends now, and a refresh of the rows
        // rebuilds this, so "now" moves along with it.
        staleBefore: new Date(
          (periodStart ?? new Date()).getTime() - staleAfterHours * 60 * 60 * 1000,
        ),
        firstRecordAt,
        t,
        language: i18n.language,
        odometerDecimals,
      }),
    [
      device,
      rows,
      effectiveFallbackRows,
      lastKnownRows,
      position,
      gpsOdometerRows,
      periodStart,
      staleAfterHours,
      firstRecordAt,
      t,
      i18n.language,
      odometerDecimals,
    ],
  );

  // The GPS block goes under the one before it rather than into a column of its own.
  const columns = useMemo(
    () =>
      groups.reduce<ChecklistGroup[][]>((cells, group) => {
        if (group.id === "gpsData" && cells.length > 0) {
          cells[cells.length - 1].push(group);
        } else {
          cells.push([group]);
        }
        return cells;
      }, []),
    [groups],
  );

  const report = useMemo(
    () => buildChecklistReport(device, groups, t, vinDetail),
    [device, groups, t, vinDetail],
  );

  return (
    <>
      <VehicleIdentification
        device={device}
        vin={vinDetail}
        activity={device ? activity : undefined}
      />
      <Paper
        variant="outlined"
        sx={{ mb: 1.5, borderRadius: 1, overflow: "hidden", flexShrink: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("liveData.checklist.title")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showMap}
                  onChange={event => setShowMap(event.target.checked)}
                />
              }
              label={t("liveData.checklist.showMap")}
            />
            <Tooltip title={t("liveData.checklist.tellTalesHint")}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showTellTales}
                    onChange={event => setShowTellTales(event.target.checked)}
                  />
                }
                label={t("liveData.checklist.tellTales")}
              />
            </Tooltip>
            <OdometerDecimalsButton>
              <StaleAfterHoursSetting />
              <TellTaleSizeSetting />
            </OdometerDecimalsButton>
            <ExcelExportButton report={report} disabled={exportBlocked} />
            <IconButton
              size="small"
              onClick={() => setIsExpanded(previous => !previous)}
              aria-label={
                isExpanded ? t("liveData.checklist.collapse") : t("liveData.checklist.expand")
              }
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </IconButton>
          </Stack>
        </Box>
        {isError ? (
          <Alert severity="error" sx={{ m: 1.5 }}>
            {t("liveData.checklist.loadError")}
          </Alert>
        ) : (
          <>
            {isExpanded && isPending && <LinearProgress />}
            {isExpanded && fallbackError && (
              <Alert severity="warning" sx={{ mx: 1.5, mb: 1.5 }}>
                {t(
                  "liveData.checklist.historyUnavailable",
                  "The last 24 hours could not be loaded. Values not reported live are shown as not found.",
                )}
              </Alert>
            )}
            {isExpanded && positionError && (
              <Alert severity="warning" sx={{ mx: 1.5, mb: 1.5 }}>
                {t("liveData.checklist.positionFailed")}
              </Alert>
            )}
            {isExpanded && (
              <Box
                ref={targetRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  px: 1.5,
                  pb: 1.5,
                  ...(heightPx === null
                    ? { overflowY: "visible" }
                    : { height: heightPx, overflowY: "auto" }),
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                      xl: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                    flexShrink: 0,
                  }}
                >
                  {columns.map(column => (
                    <Stack key={column[0].title} gap={1.5} sx={{ minWidth: 0 }}>
                      {column.map(group => (
                        <Box key={group.title}>
                          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                            {group.title}
                          </Typography>
                          <Stack gap={0.75}>
                            {group.items.map(item => (
                              <ChecklistLine key={item.label} item={item} pending={isPending} />
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ))}
                </Box>
                {/* Takes the room the drag handle gives the list, so the map grows and shrinks
                    with it; at the start, and never below the minimum, it has its own height. */}
                {showMap && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                      ...(heightPx === null
                        ? { height: MAP_HEIGHT_PX }
                        : { flex: 1, minHeight: MAP_MIN_HEIGHT_PX }),
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                      {t("liveData.checklist.liveLocation")}
                    </Typography>
                    {positionLoading ? (
                      <CircularProgress size={24} />
                    ) : positionError ? null : position ? (
                      <Suspense fallback={<CircularProgress size={24} />}>
                        <LiveLocationMap
                          position={position}
                          groupIds={groupIds}
                          isDriving={activity === "driving"}
                          bearing={statusInfo?.bearing}
                        />
                      </Suspense>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("liveData.checklist.noPosition")}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {isExpanded && showTellTales && device?.id && (
              <ChecklistTellTales deviceId={device.id} refetchInterval={statusRefreshMs} />
            )}

            {isExpanded && (
              <Box
                {...handleProps}
                role="separator"
                aria-orientation="horizontal"
                aria-label={t("liveData.checklist.resize")}
                sx={{
                  height: 10,
                  cursor: "ns-resize",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: resizing ? "action.selected" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                  touchAction: "none",
                }}
              >
                <Box sx={{ width: 36, height: 3, borderRadius: 2, bgcolor: "divider" }} />
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  );
}
